import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, useTheme, API_URL } from "../App";

// --- Helpers inline to save files ---
const resolveEnv = (str, env) => str?.replace(/\{\{(\w+)\}\}/g, (_,k) => env[k] ?? `{{${k}}}`) ?? str;

const statusColor = (code) => {
  if (!code) return "#64748b";
  if (code < 300) return "#3fb950";
  if (code < 400) return "#d29922";
  return "#f85149";
};

const prettyJson = (data) => {
  try { return JSON.stringify(typeof data==="string"?JSON.parse(data):data, null, 2); }
  catch { return String(data); }
};

function StatusPill({ code }) {
  if (!code) return null;
  const c = statusColor(code);
  return (
    <span style={{ fontFamily:"monospace", fontSize:11, fontWeight:700, padding:"2px 8px",
      borderRadius:4, background:`${c}1a`, border:`1px solid ${c}55`, color:c }}>{code}</span>
  );
}

function TabBar({ tabs, active, onSelect }) {
  const { C, accent } = useTheme();
  return (
    <div style={{ display:"flex", borderBottom:`1px solid ${C.border}` }}>
      {tabs.map(t=>(
        <button key={t} type="button" onClick={()=>onSelect(t)} style={{
          padding:"9px 16px", fontSize:12, fontWeight:600, cursor:"pointer", border:"none",
          borderBottom:active===t?`2px solid ${accent}`:"2px solid transparent",
          background:"transparent", color:active===t?C.text:C.muted,
        }}>{t}</button>
      ))}
    </div>
  );
}

function KVEditor({ rows, onChange }) {
  const { C, accent } = useTheme();
  const upd = (i,f,v) => onChange(rows.map((r,idx)=>idx===i?{...r,[f]:v}:r));
  const add  = () => onChange([...rows,{ key:"",value:"",enabled:true }]);
  const del  = (i) => onChange(rows.filter((_,idx)=>idx!==i));
  return (
    <div style={{ padding:"10px 0" }}>
      {rows.map((r,i)=>(
        <div key={i} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:7, opacity:r.enabled?1:0.45 }}>
          <input type="checkbox" checked={r.enabled} onChange={()=>upd(i,"enabled",!r.enabled)}
            style={{ accentColor:accent, cursor:"pointer", flexShrink:0 }}/>
          <input value={r.key} onChange={e=>upd(i,"key",e.target.value)} placeholder="Key"
            style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:5,
              padding:"5px 9px", color:C.text, fontSize:12, outline:"none", fontFamily:"monospace" }}/>
          <input value={r.value} onChange={e=>upd(i,"value",e.target.value)} placeholder="Value"
            style={{ flex:2, background:C.bg, border:`1px solid ${C.border}`, borderRadius:5,
              padding:"5px 9px", color:"#79c0ff", fontSize:12, outline:"none", fontFamily:"monospace" }}/>
          <button type="button" onClick={()=>del(i)} style={{ background:"none", border:"none",
            color:"#f85149", cursor:"pointer", fontSize:16, padding:"0 4px", opacity:0.5 }}>×</button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ background:"none", border:"none",
        color:"#388bfd", cursor:"pointer", fontSize:12, padding:"4px 0", fontWeight:600 }}>+ Add row</button>
    </div>
  );
}

function JsonView({ data }) {
  const { C } = useTheme();
  const html = prettyJson(data)
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/"(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?/g,
      m=>m.endsWith(":")?`<span style="color:#79c0ff">${m}</span>`:`<span style="color:#a5d6ff">${m}</span>`)
    .replace(/\b(true|false|null)\b/g,`<span style="color:#ff7b72">$1</span>`)
    .replace(/\b(-?\d+\.?\d*)\b/g,`<span style="color:#ffa657">$1</span>`);
  return (
    <pre style={{ margin:0, padding:"14px 18px", fontSize:12, lineHeight:1.75,
      fontFamily:"monospace", color:C.text, whiteSpace:"pre-wrap", wordBreak:"break-all" }}
      dangerouslySetInnerHTML={{__html:html}}/>
  );
}

// ── Main Component ──────────────────────────────────────────────
export default function RequestPanel({ initial, env, onSaved, onToggle, onDelete, onRefresh }) {
  const { user } = useAuth();
  const { C, accent, font } = useTheme();
  const api = axios.create({ baseURL: API_URL });

  const [wfName,   setWfName]   = useState("");
  const [url,      setUrl]      = useState("");
  const [method,   setMethod]   = useState("GET");
  const [schedule, setSchedule] = useState("*/5 * * * *");
  const [headers,  setHeaders]  = useState([]);
  const [params,   setParams]   = useState([]);
  const [body,     setBody]     = useState("");
  
  const [reqTab,   setReqTab]   = useState("Params");
  const [resTab,   setResTab]   = useState("Pretty");
  
  const [response, setResponse] = useState(null);
  const [sending,  setSending]  = useState(false);
  const [saveMsg,  setSaveMsg]  = useState(null);

  useEffect(()=>{
    if (initial) {
      setWfName(initial.name||""); setUrl(initial.url||""); setMethod(initial.method||"GET");
      setSchedule(initial.schedule||"*/5 * * * *");
      setHeaders(Array.isArray(initial.headers)?initial.headers:Object.entries(initial.headers||{}).map(([k,v])=>({key:k,value:v,enabled:true})));
      setParams(initial.params||[]);
      setBody(initial.body&&typeof initial.body==="object"?JSON.stringify(initial.body,null,2):(initial.body||""));
    } else {
      setWfName(""); setUrl(""); setMethod("GET"); setSchedule("*/5 * * * *"); setHeaders([]); setParams([]); setBody("");
    }
    setResponse(null);
  },[initial?.id]); // eslint-disable-line

  const resolvedUrl = resolveEnv(url, env);
  const enabledHeaders = headers.filter(h=>h.enabled&&h.key).reduce((a,h)=>{ a[h.key]=resolveEnv(h.value,env); return a; },{});

  const send = async () => {
    if (!resolvedUrl) return;
    setSending(true); setResponse(null);
    const t0 = Date.now();
    try {
      let bd = {}; if (body) try { bd=JSON.parse(body); } catch { bd=body; }
      const { data } = await api.post(`/send`, { url: resolvedUrl, method, headers: enabledHeaders, body: bd });
      setResponse(data);
    } catch (err) {
      setResponse({ status:0, statusText:err.message, data:{ error:err.message }, duration:Date.now()-t0, size:0, headers:{} });
    }
    setSending(false);
  };

  const save = async () => {
    if (!user) { setSaveMsg({ text:"Account required to save workflows", err:true }); setTimeout(()=>setSaveMsg(null),3000); return; }
    if (!wfName||!url||!schedule) { setSaveMsg({ text:"Name, URL and schedule are required", err:true }); setTimeout(()=>setSaveMsg(null),3000); return; }
    let bd = {}; if (body) { try { bd=JSON.parse(body); } catch {} }
    
    // Convert array of headers to object mappings
    const hdrs = {}; headers.filter(h=>h.key&&h.enabled).forEach(h=>hdrs[h.key]=h.value);

    try {
      await onSaved({ name:wfName, url, schedule, method, body:bd, headers: hdrs });
      setSaveMsg({ text:"✓ Saved!", err:false });
    } catch(e) {
      setSaveMsg({ text:e.message||"Save failed", err:true });
    }
    setTimeout(()=>setSaveMsg(null),3000);
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, overflow:"hidden", fontFamily:font }}>
      <div style={{ padding:"10px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:12, background:C.surface, flexShrink:0 }}>
        <input value={wfName} onChange={e=>setWfName(e.target.value)} placeholder="Request name (required to save)"
          style={{ flex:1, background:"transparent", border:"none", outline:"none", fontSize:15, fontWeight:700, color:C.text, fontFamily:font }}/>
        {initial?.id && <>
          <button className="btn btn-ghost" onClick={()=>onToggle(initial.id)} style={{fontSize: 11, padding: "4px 10px"}}>{initial.active?"⏸ Pause":"▶ Resume"}</button>
          <button className="btn btn-danger" onClick={()=>onDelete(initial.id)} style={{fontSize: 11, padding: "4px 10px"}}>Delete</button>
        </>}
        {saveMsg && <span style={{ fontSize:11, fontWeight:700, color:saveMsg.err?"#f85149":"#3fb950" }}>{saveMsg.text}</span>}
        <button className="btn btn-primary" onClick={save} style={{fontSize: 11, padding: "6px 14px"}}>💾 Save workflow</button>
      </div>

      <div style={{ padding:"10px 18px", borderBottom:`1px solid ${C.border}`, background:C.surface, flexShrink:0 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <select value={method} onChange={e=>setMethod(e.target.value)} className="input-field" style={{ width:100, fontWeight:700, cursor:"pointer" }}>
            {["GET","POST","PUT","PATCH","DELETE"].map(m=><option key={m}>{m}</option>)}
          </select>
          <input value={url} onChange={e=>setUrl(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()}
            placeholder="Enter URL  ·  use {{variable}} for env vars  ·  Enter to send" className="input-field"/>
          <button className="btn btn-send" onClick={send} disabled={sending||!url} style={{padding: "8px 22px"}}>
            {sending?"Sending…":"Send"}
          </button>
        </div>
        {url!==resolvedUrl && (
          <div style={{ marginTop:5, fontSize:11, color:"#58a6ff", fontFamily:"monospace", paddingLeft:108 }}>→ {resolvedUrl}</div>
        )}
      </div>

      <div style={{ flexShrink:0, borderBottom:`1px solid ${C.border}` }}>
        <div style={{ padding:"0 18px" }}>
          <TabBar tabs={["Params","Headers","Body","Schedule"]} active={reqTab} onSelect={setReqTab}/>
        </div>
        <div style={{ padding:"0 18px", maxHeight:190, overflowY:"auto" }}>
          {reqTab==="Params"   && <KVEditor rows={params}  onChange={setParams}/>}
          {reqTab==="Headers"  && <KVEditor rows={headers} onChange={setHeaders}/>}
          {reqTab==="Body"     && (
            <div style={{ padding:"10px 0" }}>
              {!["POST","PUT","PATCH"].includes(method)
                ? <div style={{ fontSize:12, color:C.muted, padding:"8px 0" }}>Body not used for {method}.</div>
                : <textarea value={body} onChange={e=>setBody(e.target.value)} placeholder='{"key": "value"}' rows={5}
                    className="input-field" style={{ fontFamily:"monospace", resize:"vertical", lineHeight:1.65, color:"#a5d6ff" }}/>}
            </div>
          )}
          {reqTab==="Schedule" && (
            <div style={{ padding:"12px 0", display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <label style={{ fontSize:12, color:C.muted, width:60, flexShrink:0 }}>Cron</label>
                <input value={schedule} onChange={e=>setSchedule(e.target.value)} className="input-field"/>
              </div>
              <div style={{ fontSize:11, color:C.muted, fontFamily:"monospace", background:C.surface, padding:"9px 12px", borderRadius:6, lineHeight:2 }}>
                <span style={{ color:"#79c0ff" }}>*/5 * * * *</span> every 5 min &nbsp;·&nbsp;
                <span style={{ color:"#79c0ff" }}>0 * * * *</span> hourly &nbsp;·&nbsp;
                <span style={{ color:"#79c0ff" }}>0 9 * * 1-5</span> weekdays 9am
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0 }}>
        <div style={{ padding:"8px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
          <span style={{ fontSize:11, fontWeight:700, color:C.muted, letterSpacing:0.8, textTransform:"uppercase" }}>Response</span>
          {response && <>
            <StatusPill code={response.status}/>
            <span style={{ fontSize:12, color:C.muted }}>{response.statusText}</span>
            <div style={{ marginLeft:"auto", display:"flex", gap:16, fontSize:12 }}>
              <span style={{ color:"#3fb950", fontFamily:"monospace" }}>{response.duration}ms</span>
              <span style={{ color:C.muted, fontFamily:"monospace" }}>{response.size} B</span>
            </div>
          </>}
          {!response&&!sending && <span style={{ fontSize:12, color:C.faint }}>Hit Send to see a response</span>}
          {sending && <span style={{ fontSize:12, color:"#388bfd" }}>Waiting…</span>}
          {response && <div><TabBar tabs={["Pretty","Raw","Headers"]} active={resTab} onSelect={setResTab}/></div>}
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {response && resTab==="Pretty"  && <JsonView data={response.data}/>}
          {response && resTab==="Raw"     && (
            <pre style={{ margin:0, padding:"14px 18px", fontSize:12, fontFamily:"monospace", color:C.muted, whiteSpace:"pre-wrap", wordBreak:"break-all" }}>
              {prettyJson(response.data)}
            </pre>
          )}
          {response && resTab==="Headers" && (
            <div style={{ padding:"14px 18px" }}>
              {Object.entries(response.resHeaders||response.headers||{}).map(([k,v])=>(
                <div key={k} style={{ display:"flex", gap:16, padding:"5px 0", borderBottom:`1px solid ${C.border}20`, fontSize:12 }}>
                  <span style={{ color:"#79c0ff", fontFamily:"monospace", minWidth:180 }}>{k}</span>
                  <span style={{ color:C.muted, fontFamily:"monospace", wordBreak:"break-all" }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
